import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  Typography,
  Box,
} from "@mui/material";
import { getErrorMessage } from "utils/errorHelpers";
import { AutoRenewPeriod, normalizeAutoRenewPeriod, getAutoRenewPeriodLabel } from "client/api-v2";
import { useAlerts, AlertSeverity } from "components/AlertProvider/AlertProvider";
import { createEnumChangeHandler } from "../utils/enum-utils";

interface EditAutoRenewalDialogProps {
  open: boolean;
  subscriptionName: string;
  currentAutoRenewPeriod?: AutoRenewPeriod | null;
  handleClose: () => void;
  submitAutoRenewal: (autoRenewPeriod: AutoRenewPeriod | null) => Promise<void>;
}

export function EditAutoRenewalDialog({
  open,
  subscriptionName,
  currentAutoRenewPeriod,
  handleClose,
  submitAutoRenewal,
}: EditAutoRenewalDialogProps) {
  const { sendNewAlert } = useAlerts();

  const getInitialPeriod = () => {
    const normalized = normalizeAutoRenewPeriod(currentAutoRenewPeriod);
    return normalized ?? AutoRenewPeriod.Monthly;
  };

  const [isEnabled, setIsEnabled] = useState<boolean>(
    currentAutoRenewPeriod !== null && currentAutoRenewPeriod !== undefined
  );
  const [period, setPeriod] = useState<AutoRenewPeriod>(getInitialPeriod());
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const normalized = normalizeAutoRenewPeriod(currentAutoRenewPeriod);
      setIsEnabled(normalized !== null);
      setPeriod(normalized ?? AutoRenewPeriod.Monthly);
    }
  }, [open, currentAutoRenewPeriod]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const autoRenewPeriod = isEnabled ? period : null;
      await submitAutoRenewal(autoRenewPeriod);
      handleClose();
    } catch (error) {
      sendNewAlert(getErrorMessage(error), AlertSeverity.Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentStatusText = () => {
    return getAutoRenewPeriodLabel(currentAutoRenewPeriod);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Auto-Renewal Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Subscription: <strong>{subscriptionName}</strong>
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Current Status: <strong>{getCurrentStatusText()}</strong>
          </Typography>
        </Box>

        <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
          <FormLabel component="legend">Auto-Renewal</FormLabel>
          <RadioGroup
            value={isEnabled ? "enabled" : "disabled"}
            onChange={(e) => setIsEnabled(e.target.value === "enabled")}
          >
            <FormControlLabel
              value="enabled"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">Enabled</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    Subscription will automatically renew before expiration
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="disabled"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body1">Disabled</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}
                  >
                    Subscription will expire on the term end date
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        {isEnabled && (
          <FormControl fullWidth>
            <FormLabel>Renewal Period</FormLabel>
            {
              <Select value={period} onChange={createEnumChangeHandler(AutoRenewPeriod, setPeriod)} fullWidth>
                <MenuItem value={AutoRenewPeriod.Monthly}>Monthly</MenuItem>
                <MenuItem value={AutoRenewPeriod.Annually}>Annually</MenuItem>
              </Select>
            }
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
